import type { FC, ReactNode } from 'react'

import { useState } from 'react'
import Link from 'next/link'

import {
    ChevronDown,
    HeartPulse,
    Info,
    MessageCircle,
    Minus,
    Plus
} from 'lucide-react'

import { cn } from '@/lib/utils'

type Formula = 'fox' | 'tanaka'

// Almaadawy et al. (2024, J Clin Med): Fox "220-age" came from a cohort of men
// averaging 55 and "tends to overestimate ... in young adults and underestimate
// it in older adults"; Tanaka "208 - 0.7 x age" was derived from 351 studies
// (18,712 people) with "a standard error of estimate around 11.4 bpm".
const FORMULAS: Record<Formula, { label: string; detail: string; estimate: (age: number) => number }> = {
    fox: {
        label: '220 minus age',
        detail: 'The classic formula',
        estimate: (age) => 220 - age
    },
    tanaka: {
        label: '208 minus 0.7 x age',
        detail: 'The Tanaka formula',
        estimate: (age) => 208 - 0.7 * age
    }
}

const STANDARD_ERROR_BPM = 11

interface Zone {
    key: string
    name: string
    low: number
    high: number
    talk: string | null
}

// Canning et al. (2014, PLoS One): "50-63% for light", "64-76% for moderate",
// "77-93% for vigorous effort" of maximum heart rate. The talk test wording is
// CDC's ("How to Measure Physical Activity Intensity").
const ZONES: Zone[] = [
    { key: 'light', name: 'Light', low: 0.5, high: 0.63, talk: null },
    {
        key: 'moderate',
        name: 'Moderate',
        low: 0.64,
        high: 0.76,
        talk: 'You can talk, but not sing.'
    },
    {
        key: 'vigorous',
        name: 'Vigorous',
        low: 0.77,
        high: 0.93,
        talk: 'You cannot say more than a few words without pausing for breath.'
    }
]

const MIN_AGE = 18
const MAX_AGE = 90
const DEFAULT_AGE = 40

const MIN_MEASURED = 100
const MAX_MEASURED = 230

const clampAge = (value: number): number => Math.min(MAX_AGE, Math.max(MIN_AGE, Math.round(value)))

// Plain text boxes with a numeric keypad, filtered to digits. A number input
// lets some browsers accept letters and then report an empty value, which
// looks to the visitor as if the calculator ignored what they typed.
const digitsOnly = (value: string): string => value.replace(/\D/g, '').slice(0, 3)

export const HeartRateZones: FC = (): ReactNode => {
    // `age` is always a valid age, so the results never disappear mid-typing.
    // `ageText` is what the box shows, and may briefly hold a partial entry.
    const [age, setAge] = useState(DEFAULT_AGE)
    const [ageText, setAgeText] = useState(String(DEFAULT_AGE))
    const [formula, setFormula] = useState<Formula>('fox')
    const [measuredText, setMeasuredText] = useState('')
    // The tested maximum is tucked away by default: few visitors have one, and
    // while it is filled in the age and formula play no part in the result.
    const [showMeasured, setShowMeasured] = useState(false)

    const typedAge = ageText === '' ? null : Number(ageText)
    const ageOutOfRange = typedAge === null || typedAge < MIN_AGE || typedAge > MAX_AGE

    const measured = measuredText === '' ? null : Number(measuredText)
    const measuredValid = measured !== null && measured >= MIN_MEASURED && measured <= MAX_MEASURED
    const measuredInvalid = measuredText !== '' && !measuredValid

    const estimate = Math.round(FORMULAS[formula].estimate(age))
    const other: Formula = formula === 'fox' ? 'tanaka' : 'fox'
    const otherEstimate = Math.round(FORMULAS[other].estimate(age))
    const maxHeartRate = measuredValid && measured !== null ? measured : estimate

    const chooseAge = (next: number) => {
        const clamped = clampAge(next)
        setAge(clamped)
        setAgeText(String(clamped))
    }

    const typeAge = (value: string) => {
        const digits = digitsOnly(value)
        setAgeText(digits)

        const parsed = Number(digits)
        if (digits !== '' && parsed >= MIN_AGE && parsed <= MAX_AGE) setAge(parsed)
    }

    const switchToAge = () => {
        setShowMeasured(false)
        setMeasuredText('')
    }

    const stepButton =
        'flex size-12 shrink-0 items-center justify-center rounded-xl border border-border bg-background text-foreground transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40'

    return (
        <section
            aria-label='Target heart rate calculator'
            className='my-10 overflow-hidden rounded-2xl border border-border bg-card shadow-sm'
        >
            <div className='border-b border-border bg-secondary/50 px-5 py-4 sm:px-6'>
                <div className='flex items-center gap-2'>
                    <HeartPulse className='size-5 shrink-0 text-primary' />
                    <span className='font-heading text-lg font-semibold tracking-tight text-foreground'>
                        Target heart rate calculator
                    </span>
                </div>
                <span className='mt-1 block text-sm text-muted-foreground'>
                    Set your age and your light, moderate and vigorous exercise zones update straight away.
                </span>
            </div>

            <div className='px-5 py-5 sm:px-6'>
                <div className='sm:max-w-md'>
                    <label htmlFor='heart-rate-age' className='block text-sm font-medium text-foreground'>
                        Your age
                    </label>
                    <fieldset
                        disabled={measuredValid}
                        className={cn('min-w-0 transition-opacity', measuredValid && 'opacity-50')}
                    >
                        <div className='mt-2 flex items-center gap-2'>
                            <button
                                type='button'
                                onClick={() => chooseAge(age - 1)}
                                disabled={age <= MIN_AGE}
                                aria-label='One year younger'
                                className={stepButton}
                            >
                                <Minus className='size-5' />
                            </button>
                            <input
                                id='heart-rate-age'
                                type='text'
                                inputMode='numeric'
                                pattern='[0-9]*'
                                autoComplete='off'
                                value={ageText}
                                onChange={(event) => typeAge(event.target.value)}
                                onBlur={() => setAgeText(String(age))}
                                aria-describedby='heart-rate-age-hint'
                                aria-invalid={ageOutOfRange}
                                className={cn(
                                    'h-12 w-full min-w-0 rounded-xl border bg-background px-3 text-center text-lg font-semibold text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring',
                                    ageOutOfRange ? 'border-destructive' : 'border-border'
                                )}
                            />
                            <button
                                type='button'
                                onClick={() => chooseAge(age + 1)}
                                disabled={age >= MAX_AGE}
                                aria-label='One year older'
                                className={stepButton}
                            >
                                <Plus className='size-5' />
                            </button>
                        </div>
                        <input
                            type='range'
                            min={MIN_AGE}
                            max={MAX_AGE}
                            step={1}
                            value={age}
                            onChange={(event) => chooseAge(Number(event.target.value))}
                            aria-label='Your age'
                            className='mt-4 h-2 w-full cursor-pointer accent-primary disabled:cursor-not-allowed'
                        />
                    </fieldset>
                    <span
                        id='heart-rate-age-hint'
                        className={cn(
                            'mt-1 block text-xs',
                            measuredValid
                                ? 'font-medium text-foreground'
                                : ageOutOfRange
                                    ? 'text-destructive'
                                    : 'text-muted-foreground'
                        )}
                    >
                        {measuredValid
                            ? 'Not used right now, because your tested maximum below replaces the age estimate.'
                            : ageOutOfRange
                                ? `Enter an age from ${MIN_AGE} to ${MAX_AGE}. Showing age ${age} for now.`
                                : `Type it, tap the buttons or drag the slider. For adults aged ${MIN_AGE} to ${MAX_AGE}.`}
                    </span>
                </div>

                <span className='mt-5 block text-sm font-medium text-foreground'>
                    Estimate maximum heart rate with
                    {measuredValid && <span className='ml-1 font-normal text-muted-foreground'>(not used with a tested maximum)</span>}
                </span>
                <div role='group' aria-label='Formula' className='mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2'>
                    {(Object.keys(FORMULAS) as Formula[]).map((key) => (
                        <button
                            key={key}
                            type='button'
                            aria-pressed={formula === key}
                            onClick={() => setFormula(key)}
                            disabled={measuredValid}
                            className={cn(
                                'flex flex-col items-start rounded-xl border px-4 py-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                                formula === key
                                    ? 'border-primary bg-primary text-primary-foreground'
                                    : 'border-border bg-background text-foreground hover:bg-secondary'
                            )}
                        >
                            <span className='text-sm font-semibold'>{FORMULAS[key].label}</span>
                            <span
                                className={cn(
                                    'mt-0.5 text-xs',
                                    formula === key ? 'text-primary-foreground/80' : 'text-muted-foreground'
                                )}
                            >
                                {FORMULAS[key].detail}
                            </span>
                        </button>
                    ))}
                </div>

                <div className='mt-5'>
                    <button
                        type='button'
                        onClick={() => (showMeasured ? switchToAge() : setShowMeasured(true))}
                        aria-expanded={showMeasured}
                        aria-controls='heart-rate-measured-panel'
                        className='inline-flex items-center gap-1.5 text-left text-sm font-medium text-primary hover:underline'
                    >
                        <ChevronDown className={cn('size-4 shrink-0 transition-transform', showMeasured && 'rotate-180')} />
                        {showMeasured ? 'Go back to using my age' : 'Had a supervised exercise test? Use your tested maximum instead'}
                    </button>

                    {showMeasured && (
                        <div
                            id='heart-rate-measured-panel'
                            className='mt-3 rounded-xl border border-border bg-background px-4 py-4'
                        >
                            <label htmlFor='heart-rate-measured' className='block text-sm font-medium text-foreground'>
                                Your tested maximum heart rate
                            </label>
                            <input
                                id='heart-rate-measured'
                                type='text'
                                inputMode='numeric'
                                pattern='[0-9]*'
                                autoComplete='off'
                                autoFocus
                                placeholder='For example 175'
                                value={measuredText}
                                onChange={(event) => setMeasuredText(digitsOnly(event.target.value))}
                                aria-describedby='heart-rate-measured-hint'
                                aria-invalid={measuredInvalid}
                                className={cn(
                                    'mt-2 h-12 w-full rounded-xl border bg-background px-4 text-base text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring sm:max-w-xs',
                                    measuredInvalid ? 'border-destructive' : 'border-border'
                                )}
                            />
                            <span
                                id='heart-rate-measured-hint'
                                className={cn('mt-1 block text-xs', measuredInvalid ? 'text-destructive' : 'text-muted-foreground')}
                            >
                                {measuredInvalid
                                    ? `Enter a number from ${MIN_MEASURED} to ${MAX_MEASURED} beats a minute.`
                                    : 'From a supervised exercise test. While this is filled in, your zones come from it and your age is not used.'}
                            </span>
                        </div>
                    )}
                </div>

                <div className='mt-6'>
                    <div aria-live='polite' className='rounded-xl border border-primary/40 bg-primary/5 px-4 py-4'>
                        <span className='block text-sm font-medium text-foreground'>
                            {measuredValid ? 'Your tested maximum heart rate' : `Estimated maximum heart rate at age ${age}`}
                        </span>
                        <span
                            key={maxHeartRate}
                            className='mt-1 block animate-in font-heading text-3xl font-semibold tracking-tight text-foreground fade-in duration-300 motion-reduce:animate-none'
                        >
                            {maxHeartRate} beats a minute
                        </span>
                        {measuredValid ? (
                            <span className='mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm leading-relaxed text-muted-foreground'>
                                These zones come from your tested maximum, so changing your age will not move them.
                                <button
                                    type='button'
                                    onClick={switchToAge}
                                    className='font-medium text-primary underline underline-offset-2 hover:text-foreground'
                                >
                                    Use my age instead
                                </button>
                            </span>
                        ) : (
                            <span className='mt-1 block text-sm leading-relaxed text-muted-foreground'>
                                The other formula gives {otherEstimate}. Either way, your real maximum can
                                sit around {STANDARD_ERROR_BPM} beats above or below the estimate.
                            </span>
                        )}
                    </div>

                    <div className='mt-4 flex flex-col gap-3'>
                        {ZONES.map((zone) => {
                            const low = Math.round(maxHeartRate * zone.low)
                            const high = Math.round(maxHeartRate * zone.high)

                            return (
                                <div
                                    key={zone.key}
                                    className={cn(
                                        'rounded-xl border px-4 py-3',
                                        zone.key === 'moderate' ? 'border-primary/40 bg-background' : 'border-border bg-background'
                                    )}
                                >
                                    <div className='flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1'>
                                        <span className='text-sm font-semibold text-foreground'>
                                            {zone.name}
                                            <span className='ml-2 font-normal text-muted-foreground'>
                                                {Math.round(zone.low * 100)} to {Math.round(zone.high * 100)}% of max
                                            </span>
                                        </span>
                                        <span className='font-heading text-2xl font-semibold tracking-tight text-foreground'>
                                            {low} to {high} bpm
                                        </span>
                                    </div>
                                    <span className='mt-1 block text-xs text-muted-foreground'>
                                        In a 10-second pulse count: {Math.round(low / 6)} to {Math.round(high / 6)} beats
                                    </span>
                                    {zone.talk && (
                                        <span className='mt-2 flex items-center gap-1.5 text-xs text-foreground'>
                                            <MessageCircle className='size-3.5 shrink-0 text-primary' />
                                            Talk test: {zone.talk}
                                        </span>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                </div>

                <div className='mt-5 flex gap-2 rounded-xl border border-border bg-secondary/40 px-4 py-3'>
                    <Info className='mt-0.5 size-4 shrink-0 text-muted-foreground' />
                    <span className='text-xs leading-relaxed text-muted-foreground'>
                        Some medicines, including beta blockers, slow your heartbeat, so these zones will
                        not fit. If you have a heart condition or take heart medication, ask your doctor
                        what range suits you. Nothing you enter is stored. See{' '}
                        <Link href='/movement' className='font-normal text-inherit underline underline-offset-2 hover:text-foreground'>
                            the guide to movement
                        </Link>
                        .
                    </span>
                </div>
            </div>
        </section>
    )
}

export default HeartRateZones
